// Org-level Integrations hub (/integrations).
//
// Unified tabbed page covering:
//   • Connectors — third-party integrations configured per chatbot
//   • AI Model   — org-level AI provider settings (formerly /ai-providers)
//
// Gated (route + sidebar) by the `integrations` plan feature and
// `integrations.manage` permission.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bot, Plug, ArrowRight, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { PageShell, PageStat } from "@/components/page-shell";
import { AiModelPanel } from "@/pages/ai-providers";
import type { Chatbot } from "@shared/schema";

interface IntegrationRow {
  id: string;
  type: string;
  name: string;
  status: string | null;
  configured: boolean;
}

export default function IntegrationsPage() {
  const [tab, setTab] = useState("connectors");
  const { data: chatbots, isLoading: botsLoading } = useQuery<Chatbot[]>({ queryKey: ["/api/chatbots"] });
  const { data: integrations, isLoading: intsLoading } = useQuery<IntegrationRow[]>({ queryKey: ["/api/integrations"] });

  const connected = (integrations || []).filter((i) => i.status === "connected");

  return (
    <PageShell
      eyebrow="Integrations"
      titleTestId="text-integrations-title"
      title={
        <span className="flex items-center gap-2">
          <Plug className="w-5 h-5 text-primary" />
          Integrations
        </span>
      }
      subtitle="Connect third-party services to your chatbots and configure your organization's AI model."
      stats={
        <>
          <PageStat label="Connectors" value={(integrations || []).length} hint={`${connected.length} connected`} icon={<Plug className="w-4 h-4" />} />
          <PageStat label="Chatbots" value={(chatbots || []).length} icon={<Bot className="w-4 h-4" />} />
        </>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="connectors" className="flex items-center gap-1.5">
            <Plug className="w-3.5 h-3.5" />
            Connectors
          </TabsTrigger>
          <TabsTrigger value="ai-model" className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            AI Model
          </TabsTrigger>
        </TabsList>

        {/* ── Connectors tab ───────────────────────────────────────── */}
        <TabsContent value="connectors">
          {(botsLoading || intsLoading) ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-40" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
              </div>
            </div>
          ) : (
            <>
              {(integrations || []).length > 0 && (
                <div className="mb-6">
                  <h2 className="text-sm font-semibold mb-2">Organization connectors</h2>
                  <div className="flex flex-wrap gap-2">
                    {(integrations || []).map((i) => (
                      <Badge
                        key={i.id}
                        variant={i.status === "connected" ? "default" : "outline"}
                        className="gap-1"
                        data-testid={`badge-integration-${i.type}`}
                      >
                        {i.status === "connected"
                          ? <CheckCircle2 className="w-3 h-3" />
                          : <AlertTriangle className="w-3 h-3" />}
                        {i.name || i.type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <h2 className="text-sm font-semibold mb-2">Configure by chatbot</h2>
              {(chatbots || []).length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-integrations-empty">
                  No chatbots yet. Create a chatbot first, then connect integrations to it.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {(chatbots || []).map((b: any) => (
                    <Link key={b.id} href={`/chatbots/${b.id}/integrations`} className="block">
                      <div
                        className="feature-tile h-full flex items-start justify-between gap-2"
                        data-testid={`tile-bot-integrations-${b.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <div
                              className="feature-tile-icon"
                              style={{ background: "linear-gradient(135deg, hsl(262 70% 60%), hsl(280 70% 55%))" }}
                            >
                              <Bot className="w-4 h-4" />
                            </div>
                            <div className="font-medium text-sm">{b.name}</div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Manage this bot's connectors.</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ── AI Model tab ─────────────────────────────────────────── */}
        <TabsContent value="ai-model">
          <AiModelPanel />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
